/* eslint-disable max-len */
import React, {useState, useEffect, useRef} from 'react';
import {useHistory} from 'react-router-dom';
import styled, {css, keyframes} from 'styled-components';
import ScrollContainer from 'react-indiana-drag-scroll';
import {useTranslation} from 'react-i18next';
import {useSelector, useDispatch} from 'react-redux';

import {
    Flex,
    useIsMobile,
    tabletMediaQuery,
    mobileMediaQuery,
    breakpoints,
    useBreakpoint,
    notWebMediaQuery,
    notMobileMediaQuery,
} from '../../styles';

import {ReactComponent as ArrowIcon} from './../../assets/arrow.svg';
import Illustration1Image from './../../assets/desktop_illustration_side2_side3.png';
import Illustration2Image from './../../assets/desktop_illustration_side4_side5.png';
import Illustration3Image from './../../assets/desktop_illustration_side6_side1.png';

import Illustration1ImageXmas from './../../assets/desktop_illustration_side2_side3_xmas.png';
import Illustration2ImageXmas from './../../assets/desktop_illustration_side4_side5_xmas.png';
import Illustration3ImageXmas from './../../assets/desktop_illustration_side6_side1_xmas.png';
import LiveArt1 from './../../assets/live_pp_3x_720.png';
import LiveArt2 from './../../assets/live_pp_3xtve.png';

import IllustationMobileBLImage from './../../assets/live-carousel-mobile-bl.png';
import IllustationMobileBRImage from './../../assets/live-carousel-mobile-br.png';
import IllustationMobileBImage from './../../assets/live-carousel-mobile-b.png';
import IllustationMobileTLImage from './../../assets/live-carousel-mobile-tl.png';
import IllustationMobileBLImageXmas from './../../assets/live-carousel-mobile-bl-xmas.png';
import IllustationMobileBRImageXmas from './../../assets/live-carousel-mobile-br-xmas.png';
import IllustationMobileTLImageXmas from './../../assets/live-carousel-mobile-tl-xmas.png';

import ContentBox from '../ContentBox';
import api, {graphqlQueryBuild} from '../../services/api';
import * as VideoActions from '../../store/actions/video';
import PurePlayer from '../PurePlayer';
import {selectHolidayTheme} from '../../store/reducers';

function LiveCarousel({homeLoaded}) {
    const scrollContainerRef = useRef();
    const isMobile = useIsMobile();
    const {t} = useTranslation();
    const {user, config} = useSelector(state => state);
    const dispatch = useDispatch();
    const history = useHistory();
    const breakpoint = useBreakpoint();

    const [currentSlide, setCurrentSlide] = useState(0);
    const [availableLives, setAvailableLives] = useState([]);
    const [decodedToken, setDecodedToken] = useState(undefined);
    const [userLocation, setUserLocation] = useState(undefined);
    const backgrounds = [LiveArt1, LiveArt2];
    const holidayTheme = useSelector(selectHolidayTheme);
    const illustrations = [
        <Illustration1 theme={holidayTheme} />,
        <Illustration2 theme={holidayTheme} />,
        <Illustration3 theme={holidayTheme} />,
        <Illustration1 theme={holidayTheme} />,
        <Illustration2 theme={holidayTheme} />,
        <Illustration3 theme={holidayTheme} />,
    ];

    const [screenWidth, setScreenWidth] = useState(
        window.innerWidth > 1440 ? 1440 : window.innerWidth,
    );
    const [slideWidth, setSlideWidth] = useState(
        ['sm', 'md'].includes(breakpoint) ? screenWidth + 380 : screenWidth,
    );

    const [playerReady, setPlayerReady] = useState(0);
    const [contentSrc, setContentSrc] = useState(null);
    const [preparingPlayback, setPreparingPlayback] = useState(false);

    const previewAmount = 2;
    let resizingTimeout = useRef(null);
    let autoSlideInterval = useRef(null);

    useEffect(() => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollTo(0, 0);
        }

        // eslint-disable-next-line
    }, []);

    useEffect(() => {
        if (availableLives.length > 1) {
            clearInterval(autoSlideInterval.current);
            autoSlideInterval.current = setInterval(() => {
                handleGoToSlide(currentSlide + 1);
            }, 30000);
        }

        if (playerReady !== currentSlide) setPlayerReady(0);

        if (
            availableLives[currentSlide] &&
            !availableLives[currentSlide].url &&
            !preparingPlayback
        ) {
            if (
                !contentSrc ||
                contentSrc.id !== availableLives[currentSlide].id ||
                !!contentSrc.url
            ) {
                preparePlayback(availableLives[currentSlide].id);
            }
        }

        return () => {
            if (
                user.jwtToken !== undefined &&
                availableLives[currentSlide] &&
                availableLives[currentSlide].id &&
                availableLives[currentSlide].id.indexOf('freelive') < 0 &&
                availableLives[currentSlide].id.indexOf('toontubers') < 0
            ) {
                let content_stop_url = `${(config.catalog_settings &&
                    config.catalog_settings.endpoint) ||
                    'https://avs-proxy.dev.aaa.wmgp.io/v1'}/R/${
                    user.catalogLanguage
                }/${
                    config.catalog_settings && config.catalog_settings.platforms
                        ? config.catalog_settings.platforms[0]
                        : 'PCTV_DASH'
                }/${user.catalogTenant ||
                    'CNPLUS_LATAM_LATAM'}/CONTENT/STOP?contentId=${
                    availableLives[currentSlide].id
                }&asJson=Y&type=VOD&bookmark=0&deltaThreshold=0`;

                let headers = new Headers();
                headers.append('Authorization', `Bearer ${user.jwtToken}`);

                fetch(content_stop_url, {headers}).then(res => res.json());
            }
        };

        // eslint-disable-next-line
    }, [currentSlide, availableLives]);

    useEffect(() => {
        if (userLocation) {
            loadLives();
        }

        // eslint-disable-next-line
    }, [userLocation]);

    useEffect(() => {
        handleWindowResize();

        window.addEventListener('resize', handleWindowResize);

        return () => {
            window.removeEventListener('resize', handleWindowResize);
        };
        // eslint-disable-next-line
    }, [screenWidth, breakpoint]);

    useEffect(() => {
        handleStartSlide(previewAmount);
        // eslint-disable-next-line
    }, [availableLives]);

    useEffect(() => {
        const scrollContainer = scrollContainerRef.current;

        if (scrollContainer) {
            scrollContainer.addEventListener('scroll', handleContainerScroll);
        }

        return () => {
            scrollContainer &&
                scrollContainer.removeEventListener(
                    'scroll',
                    handleContainerScroll,
                );
        };

        // eslint-disable-next-line
    }, [screenWidth, availableLives]);

    useEffect(() => {
        if (user && user.jwtToken) {
            setDecodedToken(JSON.parse(atob(user.jwtToken.split('.')[1])));
        } else {
            setUserLocation(
                user && user.countryIsoCode ? user.countryIsoCode : null,
            );
        }
    }, [user]);

    useEffect(() => {
        if (decodedToken) {
            setUserLocation(decodedToken.idp_location);
        }

        // eslint-disable-next-line
    }, [decodedToken]);

    const handleWindowResize = () => {
        clearTimeout(resizingTimeout.current);
        resizingTimeout.current = setTimeout(endOfResize, 100);

        let srcWidth = window.innerWidth > 1440 ? 1440 : window.innerWidth;
        let sldWidth = ['sm', 'md'].includes(breakpoint)
            ? srcWidth + 380
            : srcWidth;

        setScreenWidth(srcWidth);
        setSlideWidth(sldWidth);
    };

    const endOfResize = () => {
        if (scrollContainerRef.current) {
            scrollContainerRef.current.scrollLeft = previewAmount * slideWidth;
        }
    };

    const handleContainerScroll = event => {
        let scrollLeft = event.target.scrollLeft;

        const currSlidePrecision =
            Math.min(
                scrollLeft / slideWidth,
                availableLives.length - 1 + previewAmount * 2,
            ) - previewAmount;

        const currSlide = Math.round(currSlidePrecision);

        setCurrentSlide(currSlide);

        if (
            currSlidePrecision * 1.0005 >= availableLives.length ||
            currSlidePrecision <= -1
        ) {
            if (currSlide < 0) {
                handleStartSlide(availableLives.length - 1 + previewAmount);
            } else if (currSlide > availableLives.length - 1) {
                handleStartSlide(previewAmount);
            }
        }
    };

    const handleStartSlide = index => {
        const scrollContainer = scrollContainerRef.current;

        if (!scrollContainer) return;

        const slideSize = slideWidth;

        if (index < availableLives.length + previewAmount * 2) {
            scrollContainerRef.current.scrollLeft = index * slideSize;
        }
    };

    const handlePreviousSlide = () => {
        const scrollContainer = scrollContainerRef.current;

        if (!scrollContainer) return;

        const slideSize = slideWidth + (isMobile ? 8 : 16);

        if (currentSlide > 0 - previewAmount) {
            scrollContainerRef.current.scrollTo({
                left: (currentSlide - 1 + previewAmount) * slideSize,
                behavior: 'smooth',
            });
        }
    };

    const handleNextSlide = () => {
        const scrollContainer = scrollContainerRef.current;

        if (!scrollContainer) return;

        if (currentSlide < availableLives.length + previewAmount * 2) {
            scrollContainerRef.current.scrollTo({
                left: (currentSlide + 1 + previewAmount) * slideWidth,
                behavior: 'smooth',
            });
        }
    };

    const handleGoToSlide = slideIndex => {
        const scrollContainer = scrollContainerRef.current;

        if (!scrollContainer) return;

        const slideSize = slideWidth;

        const left = (slideIndex + previewAmount) * slideSize;

        if (slideIndex < availableLives.length + previewAmount) {
            scrollContainerRef.current.scrollTo({
                left,
                behavior: 'smooth',
            });
        }
    };

    const handleEndScroll = () => {
        handleGoToSlide(currentSlide);
    };

    const loadLives = async () => {
        let query = graphqlQueryBuild(
            'live',
            `
            id
            title
            description
            url
        `,
            `location:"${userLocation}"`,
        );

        let live_response = await api
            .get('', {
                params: {
                    query,
                },
            })
            .then(res => res)
            .catch(err => err.response);

        if (live_response.data.data && live_response.data.data.live) {
            let lives = live_response.data.data.live;

            setAvailableLives([...lives]);
        } else {
            setAvailableLives([]);
        }
    };

    const handleClickLive = live_id => {
        history.push(`/now/${live_id}`);
    };

    const preparePlayback = async live_id => {
        if (
            live_id.indexOf('freelive') >= 0 ||
            live_id.indexOf('toontubers') >= 0 ||
            preparingPlayback
        )
            return;

        setPreparingPlayback(true);

        let playback_response = await dispatch(
            VideoActions.preparePlayback(live_id, user.jwtToken),
        );

        if (playback_response.resultCode !== 'OK') {
            setContentSrc(null);
        } else if (playback_response.resultObj?.src) {
            setContentSrc({id: live_id, url: playback_response.resultObj?.src});
        }

        setPreparingPlayback(false);
    };

    const renderItem = (live, index, startIndex) => {
        const completeSlideIndex = Math.round(
            scrollContainerRef.current.scrollLeft / slideWidth,
        );

        const isCurrentSlide = index + startIndex === completeSlideIndex;

        let illustrationIndex = index;

        if (startIndex === 0) {
            illustrationIndex = index + (availableLives.length - previewAmount);
        }

        return (
            <LiveContainer>
                <Flex
                    justifyContent={isMobile ? 'center' : 'flex-end'}
                    flex="1"
                    position="relative"
                >
                    <ContentBox
                        title={live.title}
                        data={{id: ''}}
                        hideFavouriteButton
                        hidePlayButton
                        background={backgrounds[index]}
                        type="show"
                        live
                        action={() => handleClickLive(live.id)}
                    >
                        <TitleGradient />
                        <Subtitle>{live.title}</Subtitle>
                    </ContentBox>

                    {isCurrentSlide &&
                        homeLoaded &&
                        (!!live.url ||
                            (contentSrc && contentSrc.id === live.id)) && (
                            <PlayerCard show={playerReady === index}>
                                <PurePlayer
                                    content={{
                                        ...live,
                                        src: live.url || contentSrc.url,
                                    }}
                                    setPlayerReady={() => setPlayerReady(index)}
                                    setAdStarted={() => null}
                                    muted
                                    noAdvertising
                                />

                                <LiveIndicator>
                                    <LiveCircle title="" aria-hidden="true" />
                                    {t('liveCarousel.live')}
                                </LiveIndicator>
                            </PlayerCard>
                        )}
                </Flex>
                {!['xs', 'mobile'].includes(breakpoint) && (
                    <Flex
                        flex="1"
                        direction="column"
                        alignItems="flex-start"
                        margin={
                            breakpoint === 'sm' ? '0 0 0 43px' : '0 0 0 61px'
                        }
                        zIndex="10"
                    >
                        <Title>{t('liveCarousel.live')}</Title>
                        <Subtitle>{live.title}</Subtitle>
                    </Flex>
                )}

                {!isMobile && <>{illustrations[illustrationIndex]}</>}
            </LiveContainer>
        );
    };

    const memoizedItems = React.useMemo(
        () => (
            <>
                {[...availableLives, ...availableLives]
                    .slice(-previewAmount)
                    .map((live, index) => renderItem(live, index, 0))}

                {availableLives.map((live, index) =>
                    renderItem(live, index, previewAmount),
                )}

                {[...availableLives, ...availableLives]
                    .slice(0, previewAmount)
                    .map((live, index) =>
                        renderItem(
                            live,
                            index,
                            availableLives.length + previewAmount,
                        ),
                    )}
            </>
        ),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [
            availableLives,
            previewAmount,
            currentSlide,
            homeLoaded,
            playerReady,
            contentSrc,
        ],
    );

    return (
        <Container
            className="carousel-container"
            slideWidth={slideWidth}
            visibleWidth={
                scrollContainerRef.current &&
                scrollContainerRef.current.getBoundingClientRect().width
            }
        >
            <ScrollContainer
                horizontal={availableLives.length > 1}
                vertical={false}
                innerRef={scrollContainerRef}
                className="scroll-container"
                onEndScroll={handleEndScroll}
            >
                <Flex
                    alignItems="center"
                    justifyContent="flex-start"
                    width={`calc(${Math.min(
                        screenWidth,
                        1440,
                    )}px * ${availableLives.length + previewAmount * 2 || 1})`}
                    height={
                        isMobile
                            ? '243px'
                            : ['sm', 'md'].includes(breakpoint)
                            ? '236px'
                            : '338px'
                    }
                    direction="row"
                    wrap="no-wrap"
                >
                    {memoizedItems}
                </Flex>
            </ScrollContainer>

            {isMobile && (
                <>
                    <IllustationMobileTL theme={holidayTheme} />
                    <IllustationMobileBR theme={holidayTheme} />
                    <IllustationMobileB theme={holidayTheme} />
                    <IllustationMobileBL theme={holidayTheme} />
                </>
            )}

            {availableLives.length > 1 && (
                <>
                    <IndicatorsContainer>
                        {availableLives.map((item, index) => (
                            <SlideIndicator
                                key={`sliderIndicator_${index + 1}`}
                                onClick={() => handleGoToSlide(index)}
                                current={
                                    (currentSlide < 0
                                        ? availableLives.length + currentSlide
                                        : currentSlide >
                                          availableLives.length - 1
                                        ? currentSlide - availableLives.length
                                        : currentSlide) === index
                                }
                            />
                        ))}
                    </IndicatorsContainer>

                    <>
                        <LeftControls>
                            <ButtonPrev onClick={handlePreviousSlide}>
                                <ArrowIcon title={t('controls.previous')} />
                            </ButtonPrev>
                        </LeftControls>

                        <RightControls>
                            <ButtonNext onClick={handleNextSlide}>
                                <ArrowIcon title={t('controls.next')} />
                            </ButtonNext>
                        </RightControls>
                    </>
                </>
            )}
        </Container>
    );
}

export default LiveCarousel;

const Container = styled.div`
    position: relative;
    background: #101010;
    border: 2px solid;
    border-image-slice: 1;
    border-image-source: linear-gradient(
        90deg,
        #ffd93f 0%,
        #ffd93f 4.79%,
        #00aeef 28.08%,
        #ffd93f 38.29%,
        #ffd93f 64.14%,
        #ec008c 86.86%,
        #ffd93f 96.35%
    );
    border-left-width: 0px;
    border-right-width: 0px;
    overflow: hidden;
    margin-bottom: 43.75px;

    ::-webkit-scrollbar {
        display: none;
    }

    .scroll-container {
        padding: 0px;
        margin: 0px;
    }
`;

const LeftControls = styled.div`
    position: absolute;
    top: 50%;
    left: 0px;
    transform: translate(0, -50%);
    width: 0px;
    height: 0px;
    overflow: visible;
    z-index: 11;
`;

const RightControls = styled(LeftControls)`
    left: initial;
    right: 0px;
    transform: translate(0, -50%);
    z-index: 11;
`;

const ButtonPrev = styled.button`
    position: absolute;
    top: 50%;
    transform: translate(0, -50%);
    width: 62px;
    height: 62px;
    border: none;
    border-radius: 5px;
    background-color: rgba(255, 255, 255, 0.3);
    box-sizing: border-box;
    cursor: pointer;
    padding: 0px;

    margin: auto;
    left: 24px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding-right: 6px;
    svg {
        width: 18px;
        height: 28px;
        transform: scaleX(-1);
        path {
            fill: black;
        }
        margin: auto;
    }

    ${mobileMediaQuery} {
        display: none;
    }

    ${({disabled}) =>
        disabled &&
        css`
            cursor: initial;
            opacity: 0;
            pointer-events: none;
        `}
`;

const ButtonNext = styled(ButtonPrev)`
    padding-right: initial;
    padding-left: 6px;
    left: initial;
    right: 24px;

    svg {
        transform: initial;
    }
`;

const IndicatorsContainer = styled(Flex)`
    max-width: calc(100vw - 40px);
    position: absolute;
    bottom: 35px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 10;

    ${notWebMediaQuery} {
        bottom: 16px;
    }
`;

const SlideIndicator = styled.div`
    background: #8f8b90;
    border-radius: 4px;
    height: 4px;
    width: 16px;
    cursor: pointer;

    :not(:last-child) {
        margin-right: 8px;
    }

    ${props =>
        props.current &&
        css`
            background: #ed0080;
        `}
`;

const LiveContainer = styled.div`
    position: relative;
    display: flex;
    justify-content: center;
    flex: 1;
    height: 100%;
    width: 100vw;
    max-width: 1440px;
    flex-basis: 100vw;
    flex-grow: 1;
    flex-shrink: 0;

    @media screen and (min-width: ${breakpoints.mobile}px) and (max-width: ${breakpoints.md}px) {
        margin-right: 380px;
    }

    & > div:first-of-type > div {
        width: 353px !important;
        min-height: 198px !important;
        aspect-ratio: 353 / 198;
        padding: 0;
        z-index: 10;
        position: absolute;
        top: 50%;
        right: 0;
        transform: translateY(-50%);

        ${tabletMediaQuery} {
            width: 293px !important;
            min-height: 164px !important;
            aspect-ratio: 293 / 164;
        }

        @media screen and (min-width: ${breakpoints.sm}px) and (max-width: ${breakpoints.md}px) {
            width: 316px;
            min-height: 177px;
            aspect-ratio: 316 / 177;
        }

        ${mobileMediaQuery} {
            width: 325px !important;
            min-height: 182px !important;
            aspect-ratio: 325 / 182;

            top: 50%;
            right: initial;
            left: 50%;
            transform: translate(-50%, -50%);
        }
    }
`;

const Illustration1 = styled.img.attrs(props => {
    return {
        src:
            props.theme === 'xmas'
                ? Illustration1ImageXmas
                : Illustration1Image,
    };
})`
    z-index: 1;
    pointer-events: none;
    position: absolute;
    right: -390px;
    top: -20px;
    height: 390px;

    ${tabletMediaQuery} {
        right: -326px;
        top: -36px;
        height: 300px;
    }
`;

const Illustration2 = styled.img.attrs(props => {
    return {
        src:
            props.theme === 'xmas'
                ? Illustration2ImageXmas
                : Illustration2Image,
    };
})`
    z-index: 1;
    pointer-events: none;
    position: absolute;
    height: 670px;
    bottom: -58px;
    right: -320px;

    ${tabletMediaQuery} {
        height: 505px;
        bottom: -50px;
        right: -275px;
    }
`;

const Illustration3 = styled.img.attrs(props => {
    return {
        src:
            props.theme === 'xmas'
                ? Illustration3ImageXmas
                : Illustration3Image,
    };
})`
    z-index: 1;
    pointer-events: none;
    position: absolute;
    right: -482px;
    height: 358px;
    top: -3px;

    ${tabletMediaQuery} {
        right: -338px;
        height: 260px;
        top: -5px;
    }

    @media screen and (min-width: ${breakpoints.mobile}px) and (max-width: ${breakpoints.sm}px) {
        right: -418px;
        top: -15px;
    }
`;

const Title = styled.p`
    color: rgb(255, 255, 255);
    font-size: 24px;
    font-family: 'AvenirNextRoundedStd';
    font-weight: bold;
    line-height: 26px;
    margin: 0 0 8px 0;

    ${tabletMediaQuery} {
        font-size: 20px;
    }
`;

const Subtitle = styled.p`
    color: rgb(255, 255, 255);
    font-size: 24px;
    font-family: 'AvenirNextRoundedStd';
    font-size: 21px;
    font-weight: normal;
    line-height: 24px;
    margin: 0;
    max-width: calc(100% - 200px);

    ${tabletMediaQuery} {
        font-size: 18px;
    }

    ${mobileMediaQuery} {
        font-size: 16px;
        font-weight: bold;
        line-height: 20px;
        max-width: 277px;
        position: absolute;
        left: 12px;
        bottom: 12px;
    }
`;

const showAnimation = keyframes`
    0% {
        opacity: 0;
    }


    100% {
        opacity: 1;
    }
    `;

const PlayerCard = styled.div`
    & > * {
        width: 100% !important;
        height: 100% !important;
    }
    opacity: 0;
    position: relative;

    ${({show}) =>
        show &&
        css`
            animation: ${showAnimation} 0.25s linear;
            animation-fill-mode: forwards;
            animation-delay: 1s;
            pointer-events: none;
        `}
`;

const IllustationMobileTL = styled.img.attrs(props => {
    return {
        src:
            props.theme === 'xmas'
                ? IllustationMobileTLImageXmas
                : IllustationMobileTLImage,
    };
})`
    position: absolute;
    width: 106px;
    height: 46px;
    left: 0;
    top: 0;
    pointer-events: none;
    z-index: 6;
`;

const IllustationMobileB = styled.img.attrs(() => {
    return {
        src: IllustationMobileBImage,
    };
})`
    position: absolute;
    width: 271px;
    height: 33px;
    bottom: 0;
    left: 35;
    pointer-events: none;
    z-index: 6;
`;

const IllustationMobileBL = styled.img.attrs(props => {
    return {
        src:
            props.theme === 'xmas'
                ? IllustationMobileBLImageXmas
                : IllustationMobileBLImage,
    };
})`
    position: absolute;
    width: 52px;
    height: 140px;
    bottom: 0;
    left: 0;
    pointer-events: none;
    z-index: 6;
`;

const IllustationMobileBR = styled.img.attrs(props => {
    return {
        src:
            props.theme === 'xmas'
                ? IllustationMobileBRImageXmas
                : IllustationMobileBRImage,
    };
})`
    position: absolute;
    height: 163px;
    right: 0px;
    bottom: 0px;
    pointer-events: none;
    z-index: 6;
`;

const TitleGradient = styled.div`
    position: absolute;
    bottom: 0px;
    left: 0px;
    right: 0px;
    height: 95px;
    background: linear-gradient(
        -180deg,
        rgba(0, 0, 0, 0) 0%,
        rgba(0, 0, 0, 0.75) 100%
    );

    ${notMobileMediaQuery} {
        display: none;
    }
`;

const LiveIndicator = styled(Flex)`
    height: auto !important;
    width: auto !important;
    position: absolute;
    top: 16px;
    left: 7px;
    background-color: rgba(8, 0, 8, 0.8);
    border-radius: 2px;
    padding: 6px;

    font-size: 10px;
    font-weight: bold;
    text-transform: uppercase;
`;

const LiveCircle = styled.div`
    height: 9px;
    width: 9px;
    border-radius: 100%;
    background: rgb(237, 0, 128);
    margin-right: 5px;
`;
